<template>
  <div>
    <section class="section auth-ui-section--has-margin-top auth-ui-section--is-narrow">
      <div class="container auth-ui-container--has-centered-content">
        <v-card :classes="'auth-ui-card--is-not-full-width has-back-link'">
          <div slot="header-title">
            <router-link
              :to="signInLink"
              class="auth-ui-back-link"
            >
              <i
                class="fa fa-chevron-left card-back-link-chevron"
                aria-hidden="true"
              />
              {{ $t('auth.requestResetPasswordBackLink') }}
            </router-link>
            {{ $t('auth.requestResetPasswordTitle') }}
          </div>
          <form slot="body">
            <div class="auth-ui-form-description">
              {{ $t('auth.requestResetPasswordDescription') }}
            </div>

            <div class="field">
              <label class="label auth-ui-label">
                {{ $t('auth.requestResetPasswordEmailLabel') }}
              </label>
              <div class="control has-icons-left has-icons-right auth-ui-control-has-icons-left">
                <input
                  v-model="email"
                  :class="{ 'is-danger': errors.email }"
                  :placeholder="$t('auth.requestResetPasswordEmailPlaceholder')"
                  class="input auth-ui-input"
                  type="email"
                  autocomplete="email"
                  @blur="validateEmail()"
                  @focus="clearErrorFromField('email')"
                >
                <span class="icon is-small is-left auth-ui-icon">
                  <i class="fa fa-envelope" />
                </span>
                <span class="icon is-small is-right">
                  <i
                    :class="{ 'fa-exclamation-triangle': errors.email }"
                    class="fa"
                  />
                </span>
              </div>
              <p
                :class="{ 'is-danger': errors.email, 'auth-ui-error--is-hidden': !errors.email }"
                class="help"
              >
                {{ $t('auth.requestResetPasswordEmailValidationError') }}
              </p>
            </div>

            <div class="field is-grouped is-grouped-centered auth-ui-field--has-margin-top">
              <div class="control auth-ui-button-and-spinner-container">
                <button
                  :disabled="requestLinkIsIsDisabled"
                  class="button is-link auth-ui-button"
                  @click.prevent="sendPasswordResetLink()"
                >
                  {{ $t('auth.requestResetPasswordButton') }}
                </button>
              </div>
            </div>

            <article
              v-if="serverResponse.visible"
              class="auth-ui-card__message-container"
            >
              <div
                :class="{'is-danger': serverResponse.error, 'is-success': !serverResponse.error }"
                class="message"
              >
                <div class="message-body">
                  <button
                    class="delete auth-ui-delete--is-high"
                    aria-label="delete"
                    @click="closeMessage()"
                  />
                  <span>
                    {{ serverResponse.message }}
                  </span>
                </div>
              </div>
            </article>
          </form>
        </v-card>
      </div>
    </section>
  </div>
</template>

<script>
/* ============
* Request Reset Password Page
* ============
*
* Page where a user can request a link to reset their password.
*/

import VCard from '@/components/Card.vue';
import Request from '@/tools/Request';

export default {
  name: 'AuthRequestResetPassword',

  components: {
    VCard,
  },

  data() {
    return {
      email: '',
      buttonPressed: false, // eliminates button spam
      errors: {
        email: false,
      },
      serverResponse: {
        message: '',
        visible: false,
      },
      title: process.env.VUE_APP_PAGE_TITLE,
    };
  },

  computed: {
    requestLinkIsIsDisabled() {
      return !this.email.length || this.errors.email;
    },

    signInLink() {
      return {
        name: 'auth.sign.in',
        query: this.$store.state.route.query,
      };
    },
  },

  methods: {

    clearErrorFromField(fieldName) {
      this.errors[fieldName] = false;
    },

    validateEmail() {
      // eslint-disable-next-line
      const regEx = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      this.errors.email = !regEx.test(this.email.toLocaleLowerCase());
    },

    closeMessage() {
      this.serverResponse.visible = false;
    },

    showMessage(message = '', error = false) {
      this.serverResponse.visible = true;
      this.serverResponse.error = error;
      this.serverResponse.message = message;
    },

    sendPasswordResetLink() {
      this.validateEmail();

      if (!this.buttonPressed && !this.requestLinkIsIsDisabled) {
        this.buttonPressed = true;
        this.toggleShowSpinner(true);
        this.closeMessage();

        Request.submit(
          'POST',
          `${process.env.VUE_APP_BACKEND_URL}/send_reset_password_link`,
          {
            email: this.email.toLowerCase(),
          },
        ).then(() => {
          this.showMessage(this.$t('auth.requestResetPasswordSuccess'));
          this.toggleShowSpinner(false);
        }).catch((error) => {
          this.toggleShowSpinner(false);
          if (error === 'Unauthorized') {
            this.$router.push({ name: 'auth.sign.in' });
          } else {
            this.buttonPressed = false;
            this.showMessage(this.$t('auth.requestResetPasswordError'), true);
          }
        });
      }
    },

    /**
     * Toggles the app-wide spinner state in Vuex store.
     */
    toggleShowSpinner(isVisible = true) {
      this.$store.dispatch('general/setShowSpinner', isVisible);
    },
  },
};
</script>
